import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import FlexPublicationCard from './FlexPublicationCard';
import PublicationYearNav from './PublicationYearNav';

const FOUR_DIGIT_YEAR_RE = /\b((?:19|20)\d{2})\b/;
const EMPTY_ARRAY = [];

const coerceYear = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    return n >= 1900 && n <= 2100 ? String(n) : null;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (/^\d{4}$/.test(t)) return t;
    const m = t.match(FOUR_DIGIT_YEAR_RE);
    return m ? m[1] : null;
  }
  if (Array.isArray(raw)) {
    for (const el of raw) {
      const y = coerceYear(el);
      if (y) return y;
    }
    return null;
  }
  if (typeof raw === 'object') {
    const c = raw.title ?? raw.label ?? raw.token ?? raw.value;
    if (c != null) return coerceYear(c);
    if (raw['@id']) {
      const m = String(raw['@id']).match(FOUR_DIGIT_YEAR_RE);
      return m ? m[1] : null;
    }
    return null;
  }
  return null;
};

const buildConsecutiveYearSections = (items) => {
  const sections = [];
  for (const item of items) {
    const year = coerceYear(item.publication_year) || 'Unknown';
    const currentSection = sections[sections.length - 1];
    if (!currentSection || currentSection.year !== year) {
      sections.push({ year, items: [item] });
    } else {
      currentSection.items.push(item);
    }
  }
  return sections;
};

const PublicationTabListingView = ({
  items,
  show_author_links,
  show_year_headings,
  show_bottom_navigation,
  compact,
  hide_link,
}) => {
  const showAuthorLinks = show_author_links !== false;
  const showYearHeadings = show_year_headings !== false;
  const showBottomNavigation = show_bottom_navigation !== false;

  const listingItems = Array.isArray(items) ? items : EMPTY_ARRAY;

  const yearSections = useMemo(
    () => buildConsecutiveYearSections(listingItems),
    [listingItems],
  );

  const renderCards = (sectionItems) => (
    <div className="cards__container">
      {sectionItems.map((item) => (
        <FlexPublicationCard
          key={item['@id']}
          item={item}
          showAuthorLinks={showAuthorLinks}
          compact={compact}
          hideLink={hide_link}
        />
      ))}
    </div>
  );

  return (
    <div className="view-wrapper publication-year-archive">
      <PublicationYearNav position="top" />

      {showYearHeadings ? (
        yearSections.map(({ year, items: sectionItems }, index) => (
          <section
            key={`${year}-${index}`}
            className="publication-year-archive__section"
          >
            <h2 className="publication-year-archive__heading">{year}</h2>
            {renderCards(sectionItems)}
          </section>
        ))
      ) : (
        <section className="publication-year-archive__section">
          {renderCards(listingItems)}
        </section>
      )}

      {showBottomNavigation && <PublicationYearNav position="bottom" />}
    </div>
  );
};

PublicationTabListingView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  show_author_links: PropTypes.bool,
  show_year_headings: PropTypes.bool,
  show_bottom_navigation: PropTypes.bool,
  compact: PropTypes.bool,
  hide_link: PropTypes.bool,
};

export default PublicationTabListingView;
